import type {
  OptionProps,
  ProjectDurationAverageByGroupPoint,
  ProjectDurationBoxPlotPoint,
  ProjectDurationDistributionBucket,
  ProjectDurationTrendPoint,
  ReportsGroupBy,
} from '@models/interfaces';
import { EMPTY_VALUE } from './constants';
import { getMetricLabel, getOptionString } from './helpers';
import { PROJECT_DURATION_GROUP_TITLE_BY_GROUP_BY } from './project-duration.constants';

export interface ProjectDurationAverageByGroupChartItem
  extends ProjectDurationAverageByGroupPoint {
  groupLabel: string;
}

const getDurationGroupLabel = (
  groupId?: string | null,
  groupName?: string | null,
): string => groupName || groupId || 'Unassigned';

export const getProjectDurationAverageByGroupChartData = (
  averageByGroup: ProjectDurationAverageByGroupPoint[] = [],
): ProjectDurationAverageByGroupChartItem[] =>
  averageByGroup.map(
    (
      group: ProjectDurationAverageByGroupPoint,
    ): ProjectDurationAverageByGroupChartItem => ({
      ...group,
      groupLabel: getDurationGroupLabel(group.groupId, group.groupName),
    }),
  );

export const getProjectDurationAverageByGroupTitle = (
  groupBy?: ReportsGroupBy,
  groupByOptions: OptionProps[] = [],
  chartGroupBy?: string,
): string => {
  const selectedGroupBy = groupBy ?? (chartGroupBy as ReportsGroupBy);
  const selectedOption = groupByOptions.find(
    ({ value }: OptionProps) => value === selectedGroupBy,
  );
  const optionLabel = selectedOption
    ? getOptionString(selectedOption.viewValue)
    : '';
  const groupLabel =
    optionLabel ||
    (selectedGroupBy
      ? PROJECT_DURATION_GROUP_TITLE_BY_GROUP_BY[selectedGroupBy] ||
        getMetricLabel(selectedGroupBy)
      : 'Group');

  return `Average Duration by ${groupLabel}`;
};

export const formatDurationDays = (value?: number | null): string => {
  if (typeof value !== 'number') {
    return EMPTY_VALUE;
  }

  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

export const hasProjectDurationDistributionData = (
  distribution: ProjectDurationDistributionBucket[],
): boolean =>
  distribution.some(
    ({ projectCount }: ProjectDurationDistributionBucket) => projectCount > 0,
  );

export const hasProjectDurationTrendData = (
  trend: ProjectDurationTrendPoint[],
): boolean =>
  trend.some(({ projectCount }: ProjectDurationTrendPoint) => projectCount > 0);

export const hasProjectDurationAverageByGroupData = (
  averageByGroup: ProjectDurationAverageByGroupPoint[],
): boolean =>
  averageByGroup.some(
    ({ projectCount }: ProjectDurationAverageByGroupPoint) => projectCount > 0,
  );

export const hasProjectDurationBoxPlotData = (
  boxPlotByProjectType: ProjectDurationBoxPlotPoint[],
): boolean =>
  boxPlotByProjectType.some(
    ({ projectCount }: ProjectDurationBoxPlotPoint) => projectCount > 0,
  );
